import React, { useState } from 'react';
import { 
  Radio, 
  User, 
  Mail, 
  Phone, 
  Clock, 
  Calendar, 
  DollarSign, 
  Target, 
  CheckCircle, 
  Shield, 
  Award, 
  FileText,
  AlertTriangle,
  X
} from 'lucide-react';

const RadioShowTemplate: React.FC = () => {
  // Replace these variables with your specific show details
  const SHOW_NAME = "The Smart Money Hour";
  const HOST_NAME = "Michael Johnson";
  const OFFER_DETAILS = "Complimentary Retirement Strategy Session ($1,500 Value)";
  const SHOW_TIME_SLOT = "Weekdays 3-4pm";
  
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    bestTimeToContact: '',
    financialTopic: '',
    investmentRange: '',
    financialGoals: [] as string[],
    heardAboutUs: SHOW_TIME_SLOT,
    agreeToTerms: false
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      
      if (name === 'agreeToTerms') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      } else {
        // Handle financial goals checkboxes
        setFormData(prev => {
          const updatedGoals = [...prev.financialGoals];
          
          if (checked) {
            updatedGoals.push(value);
          } else {
            const index = updatedGoals.indexOf(value);
            if (index !== -1) {
              updatedGoals.splice(index, 1);
            }
          }
          
          return {
            ...prev,
            financialGoals: updatedGoals
          };
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    
    if (!formData.bestTimeToContact) {
      newErrors.bestTimeToContact = 'Please select a preferred contact time';
    }
    
    if (!formData.financialTopic) {
      newErrors.financialTopic = 'Please select a financial topic';
    }
    
    if (!formData.investmentRange) {
      newErrors.investmentRange = 'Please select an investment range';
    }
    
    if (formData.financialGoals.length === 0) {
      newErrors.financialGoals = 'Please select at least one financial goal';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would submit the form data to your backend
      console.log('Form submitted:', formData);
      setSubmitted(true);
    }
  };

  // Function to handle closing the popup
  const handleClosePopup = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitted(false);
  };

  const financialTopics = [
    "Retirement Planning",
    "Tax-Efficient Strategies",
    "Estate Planning",
    "Investment Management",
    "Social Security Optimization",
    "Long-Term Care Planning"
  ];

  const investmentRanges = [
    "Under $100,000",
    "$100,000 - $250,000",
    "$250,000 - $500,000",
    "$500,000 - $1,000,000",
    "Over $1,000,000"
  ];

  const financialGoals = [
    "Retirement Income Planning",
    "Reducing Tax Burden",
    "Estate & Legacy Planning",
    "Investment Growth",
    "College Funding",
    "Healthcare Planning"
  ];

  const contactTimes = [
    "Morning (9AM - 12PM)",
    "Afternoon (12PM - 4PM)",
    "Evening (4PM - 7PM)"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {!submitted ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div>
              <div className="inline-flex items-center bg-primary/10 rounded-full px-4 py-2 text-primary mb-6">
                <Radio className="h-5 w-5 mr-2" />
                As Heard On {SHOW_NAME}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Welcome {SHOW_NAME} Listeners!
              </h1>
              
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 shadow-sm">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Radio className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="italic text-gray-700 mb-2">
                      "I've personally vetted this team of financial advisors and can vouch for their integrity and expertise. Their approach to retirement planning is refreshingly straightforward and effective."
                    </p>
                    <p className="font-semibold text-gray-900">{HOST_NAME}, Host of {SHOW_NAME}</p>
                  </div>
                </div>
              </div>
              
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Exclusive Offer for Radio Listeners
                </h2>
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Award className="h-8 w-8 text-primary mr-3" />
                    <h3 className="text-xl font-semibold">{OFFER_DETAILS}</h3>
                  </div>
                  <p className="text-gray-700 mb-4">
                    As a listener of {SHOW_NAME}, you're eligible for a complimentary, no-obligation retirement strategy session with one of our senior advisors. During this session, we'll:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                      <span>Analyze your current retirement strategy</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                      <span>Identify potential tax-saving opportunities</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                      <span>Review your investment portfolio for hidden risks</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5" />
                      <span>Provide actionable recommendations tailored to your goals</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="flex items-center mb-2">
                    <Shield className="h-5 w-5 text-primary mr-2" />
                    <h3 className="font-semibold">Fiduciary Advisors</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    We're legally bound to act in your best interest at all times.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="flex items-center mb-2">
                    <Award className="h-5 w-5 text-primary mr-2" />
                    <h3 className="font-semibold">Certified Experts</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Our team includes CFP®, ChFC®, and CFA® professionals.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="flex items-center mb-2">
                    <FileText className="h-5 w-5 text-primary mr-2" />
                    <h3 className="font-semibold">SEC Registered</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Registered with the Securities and Exchange Commission.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="flex items-center mb-2">
                    <Calendar className="h-5 w-5 text-primary mr-2" />
                    <h3 className="font-semibold">25+ Years Experience</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Helping clients achieve financial security since 1998.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Right Column - Form */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Claim Your {SHOW_NAME} Listener Exclusive Offer
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.fullName ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="John Smith"
                      autoComplete="name"
                    />
                  </div>
                  {errors.fullName && (
                    <p className="mt-2 text-sm text-red-600">{errors.fullName}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.phone ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="(555) 123-4567"
                      autoComplete="tel"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.email ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="bestTimeToContact" className="block text-sm font-medium text-gray-700 mb-1">
                    Best Time to Contact <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="bestTimeToContact"
                      name="bestTimeToContact"
                      value={formData.bestTimeToContact}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.bestTimeToContact ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                    >
                      <option value="">Select a time</option>
                      {contactTimes.map((time, index) => (
                        <option key={index} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                  {errors.bestTimeToContact && (
                    <p className="mt-2 text-sm text-red-600">{errors.bestTimeToContact}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="financialTopic" className="block text-sm font-medium text-gray-700 mb-1">
                    Financial Topic/Offer Mentioned <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Target className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="financialTopic"
                      name="financialTopic"
                      value={formData.financialTopic}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.financialTopic ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                    >
                      <option value="">Select a topic</option>
                      {financialTopics.map((topic, index) => (
                        <option key={index} value={topic}>{topic}</option>
                      ))}
                    </select>
                  </div>
                  {errors.financialTopic && (
                    <p className="mt-2 text-sm text-red-600">{errors.financialTopic}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="investmentRange" className="block text-sm font-medium text-gray-700 mb-1">
                    Investment Amount Range <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="investmentRange"
                      name="investmentRange"
                      value={formData.investmentRange}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.investmentRange ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                    >
                      <option value="">Select a range</option>
                      {investmentRanges.map((range, index) => (
                        <option key={index} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                  {errors.investmentRange && (
                    <p className="mt-2 text-sm text-red-600">{errors.investmentRange}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Financial Goals <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {financialGoals.map((goal, index) => (
                      <label key={index} className="flex items-start">
                        <input
                          type="checkbox"
                          name={`goal-${index}`}
                          value={goal}
                          checked={formData.financialGoals.includes(goal)}
                          onChange={handleChange}
                          className="rounded border-gray-300 text-primary focus:ring-primary mt-1"
                        />
                        <span className="ml-2 text-sm text-gray-700">{goal}</span>
                      </label>
                    ))}
                  </div>
                  {errors.financialGoals && (
                    <p className="mt-2 text-sm text-red-600">{errors.financialGoals}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="heardAboutUs" className="block text-sm font-medium text-gray-700 mb-1">
                    How You Heard About Us
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Radio className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="heardAboutUs"
                      name="heardAboutUs"
                      value={formData.heardAboutUs}
                      onChange={handleChange}
                      className="pl-10 block w-full rounded-lg border border-gray-300 py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary"
                      placeholder={SHOW_TIME_SLOT}
                      readOnly
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="agreeToTerms"
                        name="agreeToTerms"
                        type="checkbox"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        className={`h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary ${errors.agreeToTerms ? 'border-red-300' : ''}`}
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                        I agree to be contacted about financial services and understand my information will be kept confidential in accordance with your <a href="#" className="text-primary hover:underline">Privacy Policy</a>.
                      </label>
                    </div>
                  </div>
                  {errors.agreeToTerms && (
                    <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg text-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                >
                  Claim Your {SHOW_NAME} Listener Exclusive Offer
                </button>
                
                <div className="mt-4 text-xs text-gray-500 space-y-2">
                  <p>
                    <strong>Privacy Notice:</strong> We respect your privacy and will never sell or share your information with unauthorized third parties.
                  </p>
                  <p>
                    <strong>Regulatory Disclosure:</strong> [Company Name] is a registered investment advisor. Investments involve risk and are not guaranteed. Past performance is not indicative of future results.
                  </p>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Thank You!</h3>
                <button 
                  onClick={handleClosePopup}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
              <p className="text-xl text-gray-600 mb-6">
                Your request for the {SHOW_NAME} listener exclusive offer has been received. One of our advisors will contact you shortly at your preferred time to schedule your complimentary retirement strategy session.
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What to expect next:</h3>
                <ul className="text-left space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>A financial advisor will call you within 24 hours</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Schedule your complimentary retirement strategy session</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Receive a personalized retirement analysis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>Get actionable recommendations tailored to your goals</span>
                  </li>
                </ul>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Important Note</h4>
                    <p className="mt-1 text-sm text-yellow-700">
                      This offer is exclusive to {SHOW_NAME} listeners and is only available for a limited time. Please have any relevant financial documents ready for your consultation to maximize the value of your session.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Trust Indicators Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Our Clients Trust Us</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Fiduciary Advisors</h3>
              <p className="text-gray-600">We're legally bound to act in your best interest at all times</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Certified Experts</h3>
              <p className="text-gray-600">Our team includes CFP®, ChFC®, and CFA® professionals</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <FileText className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">SEC Registered</h3>
              <p className="text-gray-600">Registered with the Securities and Exchange Commission</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Calendar className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">25+ Years Experience</h3>
              <p className="text-gray-600">Helping clients achieve financial security since 1998</p>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <div className="inline-flex flex-wrap justify-center gap-8">
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/81/CFP_mark.svg/1200px-CFP_mark.svg.png" alt="CFP" className="h-12 w-auto grayscale opacity-70" />
              </div>
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/8f/Certified_Financial_Analyst_Logo.svg/1200px-Certified_Financial_Analyst_Logo.svg.png" alt="CFA" className="h-12 w-auto grayscale opacity-70" />
              </div>
              <div className="flex items-center">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/FINRA_logo.svg/1200px-FINRA_logo.svg.png" alt="FINRA" className="h-12 w-auto grayscale opacity-70" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Regulatory Disclosures */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Disclosures</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                [Company Name] is a registered investment advisor. Registration does not imply a certain level of skill or training nor does it imply endorsement by the SEC.
              </p>
              <p>
                The complimentary retirement strategy session is designed to provide general information and is not intended to provide specific investment, tax, or legal advice. 
                Please consult with qualified professionals for advice specific to your situation.
              </p>
              <p>
                Investments involve risk and are not guaranteed. Past performance is not indicative of future results.
              </p>
              <p>
                This offer is exclusive to listeners of {SHOW_NAME} and may be withdrawn at any time without notice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadioShowTemplate;